import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TOKEN_KEY = "judo_token";

const AuthContext = createContext(null);

const readToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage unavailable — session lives only until reload
  }
};

const authHeaders = (token) => ({
  Accept: "application/json",
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(readToken);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(readToken()));

  const startSession = useCallback((newToken, newUser) => {
    writeToken(newToken);
    setToken(newToken);
    setUser(newUser);
  }, []);

  const clearSession = useCallback(() => {
    writeToken(null);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/api/auth/me`, { headers: authHeaders(token) })
      .then((res) => !cancelled && setUser(res.data))
      .catch((err) => {
        if (!cancelled && err.response?.status === 401) clearSession();
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token, clearSession]);

  // A sign-in either gives a token at once, or (2FA on) asks for a code: { twoFactor, challenge }.
  const finishSignIn = (data) => {
    if (data.two_factor) return { twoFactor: true, challenge: data.challenge };
    startSession(data.token, data.user);
    return { twoFactor: false };
  };

  const login = async (email, password) => {
    const res = await axios.post(
      `${API_BASE_URL}/api/auth/login`,
      { email, password },
      { headers: authHeaders() }
    );
    return finishSignIn(res.data);
  };

  const googleLogin = async (accessToken) => {
    const res = await axios.post(
      `${API_BASE_URL}/api/auth/google`,
      { access_token: accessToken },
      { headers: authHeaders() }
    );
    return finishSignIn(res.data);
  };

  const completeTwoFactor = async (challenge, code) => {
    const res = await axios.post(
      `${API_BASE_URL}/api/auth/two-factor-challenge`,
      { challenge, code },
      { headers: authHeaders() }
    );
    startSession(res.data.token, res.data.user);
  };

  const setupTwoFactor = async (currentPassword) => {
    const res = await axios.post(
      `${API_BASE_URL}/api/auth/two-factor/setup`,
      { current_password: currentPassword },
      { headers: authHeaders(token) }
    );
    return res.data;
  };

  const confirmTwoFactor = async (code) => {
    const res = await axios.post(
      `${API_BASE_URL}/api/auth/two-factor/confirm`,
      { code },
      { headers: authHeaders(token) }
    );
    setUser((prev) => (prev ? { ...prev, two_factor_enabled: true } : prev));
    return res.data;
  };

  const disableTwoFactor = async (currentPassword, code) => {
    await axios.delete(`${API_BASE_URL}/api/auth/two-factor`, {
      headers: authHeaders(token),
      data: { current_password: currentPassword, code },
    });
    setUser((prev) => (prev ? { ...prev, two_factor_enabled: false } : prev));
  };

  const register = async (formData) => {
    const res = await axios.post(`${API_BASE_URL}/api/auth/register`, formData, {
      headers: authHeaders(),
    });
    startSession(res.data.token, res.data.user);
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/logout`, null, {
        headers: authHeaders(token),
      });
    } catch {
      // token already invalid — just drop it locally
    }
    clearSession();
  };

  const updatePhoto = async (file) => {
    const formData = new FormData();
    formData.append("photo", file);
    const res = await axios.post(`${API_BASE_URL}/api/auth/photo`, formData, {
      headers: authHeaders(token),
    });
    setUser(res.data);
  };

  const updateProfile = async (data) => {
    const res = await axios.put(`${API_BASE_URL}/api/auth/profile`, data, {
      headers: authHeaders(token),
    });
    setUser(res.data);
  };

  const changePassword = async (data) => {
    await axios.put(`${API_BASE_URL}/api/auth/password`, data, {
      headers: authHeaders(token),
    });
  };

  const attachCard = async (belt) => {
    const res = await axios.put(
      `${API_BASE_URL}/api/auth/card`,
      { belt },
      { headers: authHeaders(token) }
    );
    setUser(res.data);
  };

  const fetchPayments = async () => {
    const res = await axios.get(`${API_BASE_URL}/api/auth/payments`, {
      headers: authHeaders(token),
    });
    return res.data;
  };

  const fetchTeam = async () => {
    const res = await axios.get(`${API_BASE_URL}/api/auth/team`, {
      headers: authHeaders(token),
    });
    return res.data;
  };

  const cancelCardRequest = async () => {
    const res = await axios.delete(`${API_BASE_URL}/api/auth/card-request`, {
      headers: authHeaders(token),
    });
    setUser(res.data);
  };

  const fetchCardRequests = async () => {
    const res = await axios.get(`${API_BASE_URL}/api/auth/trainer/card-requests`, {
      headers: authHeaders(token),
    });
    return res.data;
  };

  const decideCardRequest = async (id, decision) => {
    await axios.post(`${API_BASE_URL}/api/auth/trainer/card-requests/${id}/${decision}`, null, {
      headers: authHeaders(token),
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: Boolean(user),
        login,
        googleLogin,
        completeTwoFactor,
        setupTwoFactor,
        confirmTwoFactor,
        disableTwoFactor,
        register,
        logout,
        updatePhoto,
        updateProfile,
        changePassword,
        attachCard,
        cancelCardRequest,
        fetchTeam,
        fetchPayments,
        fetchCardRequests,
        decideCardRequest,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
